/**
 * funnel-checkout.js
 *
 * Handles the pre-checkout funnel page (Page 3):
 *  - Reads ?variant=ID&qty=N URL params to pre-select a variant card
 *  - Updates the order summary when a variant card is selected
 *  - Validates and submits the shipping form
 *  - TIER 3 (active): calls a serverless function that creates a Shopify Draft
 *    Order with the shipping address pre-filled, then redirects to invoice_url.
 *    No Shopify Plus required. No address re-entry at checkout.
 *
 * Checkout tier overview
 * ──────────────────────
 *  Tier 2 (fallback): Cart AJAX → note_attributes → /checkout?email=…
 *    Address stored in order notes; customer re-enters at native checkout.
 *    Safe to re-enable by replacing initForm()'s try block with TIER 2 CODE.
 *
 *  Tier 3 (active):   POST /api/create-draft-order → invoice_url redirect.
 *    Full address baked into the draft order; customer lands directly at payment.
 *    Requires SHOPIFY_STORE_DOMAIN + SHOPIFY_ADMIN_ACCESS_TOKEN on the function host.
 *    Configure the endpoint URL via the "Draft Order Endpoint URL" section setting.
 *
 *  Tier 4 (future, Plus-only): Storefront Cart API cartCreate + cartDeliveryAddressesAdd
 *    See: https://shopify.dev/docs/storefronts/headless/building-with-the-storefront-api/cart/manage
 */

(function () {
  'use strict';

  /* ─── State ─────────────────────────────────────── */
  let selectedVariantId = null;
  let selectedQty = 1;
  let variantData = {};   // { variantId: { price, comparePrice, title, image } }

  /* ─── Helpers ────────────────────────────────────── */
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function getParam(name) {
    return new URLSearchParams(window.location.search).get(name);
  }

  function qs(selector, root) {
    return (root || document).querySelector(selector);
  }

  function qsa(selector, root) {
    return Array.from((root || document).querySelectorAll(selector));
  }

  /* ─── Variant card selection ─────────────────────── */
  function selectVariantCard(variantId) {
    selectedVariantId = variantId;
    const cards = qsa('.fc-variant-card');
    cards.forEach(function (card) {
      const isActive = String(card.dataset.variantId) === String(variantId);
      card.classList.toggle('fc-variant-card--active', isActive);
    });
    updateOrderSummary(variantId);
  }

  function initVariantCards() {
    const cards = qsa('.fc-variant-card');

    cards.forEach(function (card) {
      const id = card.dataset.variantId;
      variantData[id] = {
        price: parseInt(card.dataset.price, 10),
        comparePrice: parseInt(card.dataset.comparePrice, 10) || 0,
        title: card.dataset.title || '',
        image: card.dataset.image || '',
        qty: parseInt(card.dataset.qty, 10) || 1,
      };

      const btn = card.querySelector('.fc-variant-card__btn');
      if (btn) btn.dataset.original = btn.textContent;

      card.addEventListener('click', function () {
        selectedQty = variantData[id] ? variantData[id].qty : 1;
        selectVariantCard(id);
      });
    });

    // Pre-select: URL param → data-default-selected card → first card
    const urlVariant = getParam('variant');
    const urlQty = parseInt(getParam('qty'), 10);
    if (urlQty && !isNaN(urlQty)) selectedQty = urlQty;

    if (urlVariant && variantData[urlVariant]) {
      selectVariantCard(urlVariant);
    } else {
      const defaultCard =
        cards.find(function (c) { return c.dataset.defaultSelected === 'true'; }) ||
        cards[0];
      if (defaultCard) {
        const defaultId = defaultCard.dataset.variantId;
        selectedQty = variantData[defaultId] ? variantData[defaultId].qty : 1;
        selectVariantCard(defaultId);
      }
    }
  }

  /* ─── Order summary update ───────────────────────── */
  function updateOrderSummary(variantId) {
    const data = variantData[variantId];
    if (!data) return;

    const summaryTitle = qs('.fc-summary__title');
    const summaryImg = qs('.fc-summary__img');
    const summaryOriginal = qs('.fc-summary__original-price');
    const summaryDiscount = qs('.fc-summary__discount');
    const summarySubtotal = qs('.fc-summary__subtotal');
    const summaryQty = qs('.fc-summary__qty');
    const summaryDiscountLabel = qs('.fc-summary__discount-label');
    const summaryVariantPrice = qs('.fc-summary__variant-price');

    if (summaryTitle) summaryTitle.textContent = data.title;
    if (summaryImg && data.image) summaryImg.src = data.image;
    if (summaryQty) summaryQty.textContent = 'QTY: ' + selectedQty;

    if (data.comparePrice && data.comparePrice > data.price) {
      const savings = data.comparePrice - data.price;
      const savingsPct = Math.round(savings * 100 / data.comparePrice);
      if (summaryOriginal) summaryOriginal.textContent = formatMoney(data.comparePrice * selectedQty);
      if (summaryDiscount) summaryDiscount.textContent = '-' + formatMoney(savings * selectedQty);
      if (summarySubtotal) summarySubtotal.textContent = formatMoney(data.price * selectedQty);
      if (summaryDiscountLabel) summaryDiscountLabel.textContent = '+' + savingsPct + '% OFF';
      if (summaryVariantPrice) summaryVariantPrice.textContent = formatMoney(data.price);
    } else {
      if (summaryOriginal) summaryOriginal.textContent = formatMoney(data.price * selectedQty);
      if (summaryDiscount) summaryDiscount.textContent = '';
      if (summarySubtotal) summarySubtotal.textContent = formatMoney(data.price * selectedQty);
      if (summaryDiscountLabel) summaryDiscountLabel.textContent = '';
      if (summaryVariantPrice) summaryVariantPrice.textContent = formatMoney(data.price);
    }
  }

  /* ─── Countdown timer ────────────────────────────── */
  function initCountdown() {
    const display = qs('.fc-countdown__display');
    if (!display) return;

    const storageKey = 'funnel_countdown_end';
    let endTime = parseInt(sessionStorage.getItem(storageKey), 10);

    if (!endTime || isNaN(endTime) || endTime < Date.now()) {
      endTime = Date.now() + 15 * 60 * 1000; // 15 min
      sessionStorage.setItem(storageKey, endTime);
    }

    function tick() {
      const diff = Math.max(0, endTime - Date.now());
      const m = Math.floor(diff / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      display.textContent = String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0');
      if (diff > 0) setTimeout(tick, 1000);
    }
    tick();
  }

  /* ─── FAQ accordion ──────────────────────────────── */
  function initFaq() {
    qsa('.fc-faq').forEach(function (faqRoot) {
      const faqItems = qsa('.fc-faq__item', faqRoot);
      const headerToggle = qs('.fc-faq__header-toggle', faqRoot);
      const itemsWrap = qs('.fc-faq__items-wrap', faqRoot);

      /* ── Section-level expand/collapse (header icon) ─────────────────
       * Hides/shows the entire items container. Does NOT touch individual
       * item open states so they are remembered across toggle cycles.
       * ────────────────────────────────────────────────────────────────*/
      function setSectionExpanded(expanded) {
        faqRoot.classList.toggle('fc-faq--collapsed', !expanded);
        if (headerToggle) headerToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        if (itemsWrap) {
          if (expanded) {
            // Remove cap so container grows to its natural full height.
            itemsWrap.style.maxHeight = 'none';
          } else {
            // Snapshot current rendered height first so the transition animates from real height → 0.
            itemsWrap.style.maxHeight = itemsWrap.scrollHeight + 'px';
            // Force reflow, then set to 0 to trigger CSS transition.
            itemsWrap.getBoundingClientRect();
            itemsWrap.style.maxHeight = '0';
          }
        }
      }

      /* ── Individual item open/close ─────────────────────────────────*/
      faqItems.forEach(function (item) {
        const trigger = item.querySelector('.fc-faq__question');
        const answer = item.querySelector('.fc-faq__answer');
        if (!trigger || !answer) return;

        // Default: every item open.
        item.classList.add('fc-faq__item--open');
        answer.style.maxHeight = answer.scrollHeight + 'px';

        trigger.addEventListener('click', function () {
          const isOpen = item.classList.contains('fc-faq__item--open');
          item.classList.toggle('fc-faq__item--open', !isOpen);
          answer.style.maxHeight = isOpen ? null : (answer.scrollHeight + 'px');

          // Keep the section-level wrapper height in sync only when it has a fixed cap.
          if (itemsWrap && itemsWrap.style.maxHeight !== 'none') {
            itemsWrap.style.maxHeight = itemsWrap.scrollHeight + 'px';
          }
        });
      });

      // Default state: section expanded, showing all items.
      setSectionExpanded(true);

      if (headerToggle) {
        headerToggle.addEventListener('click', function () {
          const isExpanded = headerToggle.getAttribute('aria-expanded') === 'true';
          setSectionExpanded(!isExpanded);
        });
      }

      window.addEventListener('resize', function () {
        if (!itemsWrap) return;
        const isCollapsed = faqRoot.classList.contains('fc-faq--collapsed');
        if (!isCollapsed) itemsWrap.style.maxHeight = 'none';

        faqItems.forEach(function (item) {
          if (!item.classList.contains('fc-faq__item--open')) return;
          const answer = item.querySelector('.fc-faq__answer');
          if (answer) answer.style.maxHeight = answer.scrollHeight + 'px';
        });
      });
    });
  }

  /* ─── Upsell toggle ──────────────────────────────── */
  function initUpsell() {
    const upsellCheckbox = qs('#fc-upsell-checkbox');
    if (!upsellCheckbox) return;
    // No action needed — the checkbox state is read at form submit time
  }

  /* ─── Form validation ────────────────────────────── */
  function validateForm(form) {
    const required = form.querySelectorAll('[required]');
    let valid = true;
    required.forEach(function (field) {
      field.classList.remove('fc-field--error');
      if (!field.value.trim()) {
        field.classList.add('fc-field--error');
        valid = false;
      }
    });

    const email = form.querySelector('[name="contact_email"]');
    if (email && email.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value)) {
      email.classList.add('fc-field--error');
      valid = false;
    }

    return valid;
  }

  /* ─── TIER 2 — Cart AJAX helpers (kept as fallback) ─────────────────────
   * Not called in the active Tier 3 flow. To revert, uncomment the
   * TIER 2 FALLBACK block at the bottom of initForm().
   * ─────────────────────────────────────────────────────────────────────── */
  function cartClear() {
    return fetch('/cart/clear.js', { method: 'POST' }).then(function (r) { return r.json(); });
  }

  function cartAdd(variantId, quantity, properties) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: quantity, properties: properties || {} }],
      }),
    }).then(function (r) { return r.json(); });
  }

  function cartUpdateAttributes(attributes) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
    }).then(function (r) { return r.json(); });
  }

  /* ─── TIER 3 — Draft Order API call ─────────────────────────────────────
   * POSTs to the endpoint set via the section setting "Draft Order Endpoint URL"
   * (rendered as data-draft-order-endpoint on #fc-shipping-form).
   * Falls back to /api/create-draft-order if the attribute is absent.
   * Returns { checkoutUrl, draftOrderId } — see api/create-draft-order.js.
   * ─────────────────────────────────────────────────────────────────────── */
  function createDraftOrder(form, payload) {
    var endpoint = form.dataset.draftOrderEndpoint || '/api/create-draft-order';
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (r) {
      return r.text().then(function (raw) {
        var data = {};
        try {
          data = raw ? JSON.parse(raw) : {};
        } catch (parseErr) {
          data = { error: raw || ('HTTP ' + r.status) };
        }
        if (!r.ok) throw new Error(data.error || ('HTTP ' + r.status));
        if (!data || !data.checkoutUrl) {
          throw new Error('Draft order API response is missing checkoutUrl');
        }
        return data;
      });
    });
  }

  /* ─── Form submission ────────────────────────────── */
  function initForm() {
    var form = qs('#fc-shipping-form');
    if (!form) return;

    var submitBtn = form.querySelector('[type="submit"]');
    var paypalBtn = form.querySelector('.fc-paypal-btn');
    if (submitBtn) submitBtn.dataset.original = submitBtn.textContent;
    if (paypalBtn) paypalBtn.dataset.originalAriaLabel = paypalBtn.getAttribute('aria-label') || 'Pay with PayPal';

    function setProcessingState(isProcessing, source) {
      if (submitBtn) {
        submitBtn.disabled = isProcessing;
        submitBtn.textContent = isProcessing
          ? 'Processing...'
          : (submitBtn.dataset.original || 'COMPLETE SECURE PURCHASE');
      }
      if (paypalBtn) {
        paypalBtn.classList.toggle('fc-paypal-btn--disabled', !!isProcessing);
        paypalBtn.setAttribute('aria-disabled', isProcessing ? 'true' : 'false');
        paypalBtn.setAttribute(
          'aria-label',
          isProcessing
            ? 'Processing PayPal checkout'
            : (paypalBtn.dataset.originalAriaLabel || 'Pay with PayPal')
        );
        paypalBtn.style.pointerEvents = isProcessing ? 'none' : '';
      }
      if (source === 'paypal' && paypalBtn) {
        paypalBtn.dataset.processing = isProcessing ? 'true' : 'false';
      }
    }

    function submitCheckout(options) {
      var source = options && options.source ? options.source : 'card';
      var ignoreShippingAddress = !!(options && options.ignoreShippingAddress);
      if (!selectedVariantId) {
        alert('Please select a package first.');
        return;
      }

      if (!ignoreShippingAddress && !validateForm(form)) {
        var firstError = form.querySelector('.fc-field--error');
        if (firstError) firstError.focus();
        return;
      }

      setProcessingState(true, source);

      var fd = new FormData(form);

      var upsellCheckbox = qs('#fc-upsell-checkbox');
      var upsellVariantId = upsellCheckbox && upsellCheckbox.checked
        ? upsellCheckbox.dataset.variantId
        : null;

      /* ── TIER 3 — active ─────────────────────────────────────────────── */
      var payload = {
        variantId:  selectedVariantId,
        qty:        selectedQty || 1,
        email:      fd.get('contact_email') || '',
        firstName:  fd.get('first_name')    || '',
        lastName:   fd.get('last_name')     || '',
        phone:      fd.get('phone')         || '',
        address1:   fd.get('address1')      || '',
        address2:   fd.get('address2')      || '',
        city:       fd.get('city')          || '',
        province:   fd.get('province')      || '',
        zip:        fd.get('zip')           || '',
        country:    fd.get('country')       || '',
        ignoreShippingAddress: ignoreShippingAddress,
        ignore_shipping_address: ignoreShippingAddress,
        skipShippingAddress: ignoreShippingAddress,
        skip_shipping_address: ignoreShippingAddress,
      };

      if (upsellVariantId) {
        payload.upsellVariantId = upsellVariantId;
        payload.upsellQty = parseInt(upsellCheckbox.dataset.qty || '1', 10);
      }

      createDraftOrder(form, payload)
        .then(function (data) {
          if (window.__fcSetStep) window.__fcSetStep(4);
          window.location.href = data.checkoutUrl;
        })
        .catch(function (err) {
          console.error('[funnel-checkout] Draft order error:', err);
          setProcessingState(false, source);
          alert(err && err.message ? err.message : 'Something went wrong. Please try again.');
        });

      /* ── TIER 2 FALLBACK — uncomment to revert to cart-based checkout ──
      var shippingAttrs = {
        'Shipping Email': fd.get('contact_email') || '',
        'First Name':     fd.get('first_name')    || '',
        'Last Name':      fd.get('last_name')      || '',
        'Phone':          fd.get('phone')           || '',
        'Address':        fd.get('address1')        || '',
        'Apt/Suite':      fd.get('address2')        || '',
        'City':           fd.get('city')            || '',
        'State':          fd.get('province')        || '',
        'Zip Code':       fd.get('zip')             || '',
        'Country':        fd.get('country')         || '',
      };
      cartClear()
        .then(function () { return cartAdd(parseInt(selectedVariantId, 10), selectedQty || 1, {}); })
        .then(function () {
          if (!upsellVariantId) return;
          return cartAdd(parseInt(upsellVariantId, 10), parseInt(upsellCheckbox.dataset.qty || '1', 10), {});
        })
        .then(function () { return cartUpdateAttributes(shippingAttrs); })
        .then(function () {
          var email = encodeURIComponent(fd.get('contact_email') || '');
          window.location.href = '/checkout' + (email ? '?checkout[email]=' + email : '');
        })
        .catch(function (err) {
          console.error('[funnel-checkout] Cart error:', err);
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = submitBtn.dataset.original || 'COMPLETE SECURE PURCHASE';
          }
          alert('Something went wrong. Please try again.');
        });
      ── END TIER 2 FALLBACK ── */
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      submitCheckout({ source: 'card', ignoreShippingAddress: false });
    });

    if (paypalBtn) {
      paypalBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (paypalBtn.dataset.processing === 'true') return;
        submitCheckout({ source: 'paypal', ignoreShippingAddress: true });
      });
    }
  }

  /* ─── Position each step to align with its corresponding content section ── */
  function initStepsPosition() {
    var stepsEl = qs('.fc-steps');
    var layoutOuter = qs('.fc-layout-outer');
    var stepEls = qsa('.fc-step');
    if (!stepsEl || !layoutOuter || stepEls.length < 4) return;

    var firstCard    = qs('.fc-variant-card');
    var formBoxTitle = qs('.fc-form-box__title');
    var paymentHeader = qs('.fc-payment-header');
    var submitBtn    = qs('.fc-submit-btn');
    var circleH      = 66;

    function resetStyles() {
      stepEls.forEach(function (step) {
        step.style.top = '';
        var connector = step.querySelector('.fc-step__connector');
        if (connector) connector.style.height = '';
      });
      stepsEl.style.height = '';
    }

    function update() {
      if (window.innerWidth < 1024) {
        resetStyles();
        return;
      }

      var outerRect = layoutOuter.getBoundingClientRect();
      var tops = [];

      if (firstCard) {
        var cardRect = firstCard.getBoundingClientRect();
        tops[0] = cardRect.top + cardRect.height / 2 - outerRect.top - circleH / 2;
      }

      if (formBoxTitle) {
        var titleRect = formBoxTitle.getBoundingClientRect();
        tops[1] = titleRect.top + titleRect.height / 2 - outerRect.top - circleH / 2;
      }

      if (paymentHeader) {
        var pHeadRect = paymentHeader.getBoundingClientRect();
        tops[2] = pHeadRect.top + pHeadRect.height / 2 - outerRect.top - circleH / 2;
      }

      if (submitBtn) {
        var btnRect = submitBtn.getBoundingClientRect();
        tops[3] = btnRect.top + btnRect.height / 2 - outerRect.top - circleH / 2;
      }

      // Apply initial top positions so we can read the label rects correctly
      stepEls.forEach(function (step, i) {
        if (tops[i] != null) {
          step.style.top = Math.max(0, tops[i]) + 'px';
        }
      });

      for (var i = 0; i < stepEls.length - 1; i++) {
        var connector = stepEls[i].querySelector('.fc-step__connector');
        var label = stepEls[i].querySelector('.fc-step__label');
        var labelSpace = label ? label.offsetHeight + 24 : 0; /* 12px margin-top + 12px margin-bottom */
        if (connector && tops[i] != null && tops[i + 1] != null) {
          var gap = tops[i + 1] - tops[i] - 78 - labelSpace;
          connector.style.height = Math.max(0, gap) + 'px';
        }
      }

      var maxTop = Math.max.apply(null, tops.filter(function (t) { return t != null; }));
      stepsEl.style.height = (maxTop + circleH + 40) + 'px';
    }

    update();
    window.addEventListener('resize', update, { passive: true });
    window.addEventListener('load', update);
  }

  /* ─── Scroll-driven step indicator ──────────────── */
  function initStepsObserver() {
    var stepEls = qsa('.fc-step');

    var CHECKMARK = `<svg xmlns="http://www.w3.org/2000/svg" width="66" height="66" viewBox="0 0 66 66" fill="none">
  <circle cx="32.5" cy="33.5" r="32.5" fill="white"/>
  <mask id="mask0_10_360" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="0" y="0" width="66" height="66">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M0 0H66V66H0V0Z" fill="white"/>
  </mask>
  <g mask="url(#mask0_10_360)">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M29.2987 47.3654C36.0552 40.5896 42.8147 33.8107 49.4343 27.1719C47.6299 25.3254 45.742 23.3937 43.7625 21.3685C38.9777 26.1683 34.1721 30.989 29.304 35.8723C26.8984 33.462 24.5027 31.0617 22.1884 28.7427C20.2197 30.714 18.3074 32.6291 16.4174 34.5216C20.6901 38.7822 25.0252 43.1044 29.2987 47.3654ZM66 33.0031C65.9978 51.2111 51.1997 66.0046 32.9939 66C14.7802 65.995 -0.00171864 51.2076 1.49876e-07 32.9936C0.00171894 14.7785 14.7866 -0.00223417 33.003 2.53302e-07C51.2151 0.00223467 66.0026 14.7924 66 33.0031Z" fill="#1185FA"/>
  </g>
</svg>`;
    var CHECKMARK_SM = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 12.5L9.5 17L19 8" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    var origNums = stepEls.map(function (step) {
      var num = step.querySelector('.fc-step__num');
      return num ? num.textContent.trim() : '';
    });

    var mobileSteps = qsa('.fc-mobile-step');
    var mobileOrigNums = mobileSteps.map(function (step) {
      var circle = step.querySelector('.fc-mobile-step__circle');
      return circle ? circle.textContent.trim() : '';
    });

    /* Anchor elements for steps 2, 3, 4 */
    var anchorEls = [
      qs('.fc-form-box__title'),   /* step 2 — Shipping Information */
      qs('.fc-payment-header'),    /* step 3 — Payment              */
      qs('.fc-submit-btn')         /* step 4 — Place Order          */
    ];

    var currentStep = -1;

    function applyStep(n) {
      if (n === currentStep) return;
      currentStep = n;

      stepEls.forEach(function (step, i) {
        var stepNum = i + 1;
        var numEl = step.querySelector('.fc-step__num');
        step.classList.remove('fc-step--done', 'fc-step--active');
        if (stepNum === 1 || stepNum < n) {
          step.classList.add('fc-step--done');
          if (numEl) numEl.innerHTML = CHECKMARK;
        } else if (stepNum === n) {
          step.classList.add('fc-step--active');
          if (numEl && stepNum > 1) numEl.innerHTML = CHECKMARK;
        } else {
          if (numEl && stepNum > 1) numEl.textContent = origNums[i];
        }
      });

      mobileSteps.forEach(function (step, i) {
        var stepNum = i + 1;
        var circleEl = step.querySelector('.fc-mobile-step__circle');
        step.classList.remove('fc-mobile-step--done', 'fc-mobile-step--active');
        if (stepNum === 1 || stepNum < n) {
          step.classList.add('fc-mobile-step--done');
          if (circleEl) circleEl.innerHTML = CHECKMARK_SM;
        } else if (stepNum === n) {
          step.classList.add('fc-mobile-step--active');
          if (circleEl && stepNum > 1) circleEl.innerHTML = CHECKMARK_SM;
        } else {
          if (circleEl && stepNum > 1) circleEl.textContent = mobileOrigNums[i];
        }
      });
    }

    function getActiveStep() {
      /* Trigger when the anchor element reaches the bottom 15% of the viewport (i.e. just scrolled into view) */
      var triggerPoint = window.innerHeight * 0.85;
      /* Work backwards: highest step whose anchor has entered the viewport */
      for (var i = anchorEls.length - 1; i >= 0; i--) {
        var el = anchorEls[i];
        if (el && el.getBoundingClientRect().top <= triggerPoint) {
          return i + 2; /* anchorEls[0] → step 2, [1] → step 3, [2] → step 4 */
        }
      }
      return 1; /* step 1 always done; nothing else active until scrolled to */
    }

    window.addEventListener('scroll', function () { applyStep(getActiveStep()); }, { passive: true });
    applyStep(getActiveStep());

    window.__fcSetStep = applyStep;
  }

  /* ─── Sticky order summary on desktop ───────────── */
  function initStickySummary() {
    const summary = qs('.fc-right');
    if (!summary) return;

    function update() {
      if (window.innerWidth < 1024) {
        summary.style.position = '';
        summary.style.top = '';
        summary.style.alignSelf = '';
      } else {
        summary.style.position = 'sticky';
        summary.style.top = '20px';
        summary.style.alignSelf = 'flex-start';
      }
    }

    update();
    window.addEventListener('resize', update, { passive: true });
  }

  /* ─── Boot ───────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {
    initVariantCards();
    initCountdown();
    initFaq();
    initUpsell();
    initForm();
    initStickySummary();
    initStepsPosition();
    initStepsObserver();
  });
})();
